/** Datas de campanha: legado usa `dd/mm/yyyy` no cron; o form usa `YYYY-MM-DD`. */

import { fromServerDate } from '@/shared/lib/server-date';

/** `YYYY-MM-DD` → `dd/mm/yyyy` (API / agenda.js). */
export function toApiBrDate(isoYmd?: string): string {
  if (!isoYmd?.trim()) return '';
  const raw = isoYmd.trim().slice(0, 10);
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return raw;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

/** `dd/mm/yyyy` ou ISO → `YYYY-MM-DD` (DatePicker / input). Parse UTC para ISO. */
export function fromApiBrDate(value?: string): string {
  if (!value?.trim()) return '';
  const raw = value.trim();
  const br = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (br) return `${br[3]}-${br[2]}-${br[1]}`;
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  return fromServerDate(raw);
}

/** Exibe data sem shift de timezone (date-only ou ISO). */
export function formatCampaignDate(value?: string | null, fallback = ''): string {
  if (!value) return fallback;
  const raw = String(value).trim();
  const br = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (br) return `${br[1]}/${br[2]}/${br[3]}`;
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return fallback;
  return d.toLocaleDateString('pt-BR');
}

export function formatCampaignCreatedAt(value?: string | null): { date: string; time: string } {
  if (!value) return { date: '', time: '' };
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { date: formatCampaignDate(value), time: '' };
  return {
    date: d.toLocaleDateString('pt-BR'),
    time: d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
  };
}

/** Data/hora de disparo a partir da frequência. */
export function campaignDispatchMeta(row: {
  frequencia?: string;
  frequencia_unico?: string;
  frequencia_horas?: string;
  frequencia_dia?: string | number;
  frequencia_dia_semana?: string;
}): { dateLabel: string; timeLabel: string } {
  const time = (row.frequencia_horas ?? '').trim();
  const timeLabel = time.length <= 5 ? time : time.slice(0, 5);

  if (row.frequencia === '31' && row.frequencia_unico) {
    return { dateLabel: formatCampaignDate(row.frequencia_unico), timeLabel };
  }
  if (row.frequencia === '30' && row.frequencia_dia != null && String(row.frequencia_dia)) {
    return { dateLabel: `Dia ${row.frequencia_dia}`, timeLabel };
  }
  if (row.frequencia === '7' && row.frequencia_dia_semana) {
    return { dateLabel: row.frequencia_dia_semana, timeLabel };
  }
  if (row.frequencia === '1') {
    return { dateLabel: 'Diário', timeLabel };
  }
  return { dateLabel: '', timeLabel };
}

/** Única ainda não disparada (data+hora no futuro). */
export function isUniqueCampaignPending(row: {
  frequencia?: string;
  frequencia_unico?: string;
  frequencia_horas?: string;
}): boolean {
  if (row.frequencia !== '31' || !row.frequencia_unico) return false;
  const ymd = fromApiBrDate(row.frequencia_unico);
  if (!ymd) return false;
  const [y, m, d] = ymd.split('-').map(Number);
  const [hh = 0, mm = 0] = (row.frequencia_horas ?? '00:00').split(':').map((x) => Number(x) || 0);
  const when = new Date(y, m - 1, d, hh, mm, 0, 0);
  return when.getTime() > Date.now();
}

/** Pode parar: recorrente sem expirar, ou única ainda não disparada. */
export function canStopAutomatedCampaign(row: {
  active?: boolean;
  frequencia?: string;
  expirar?: boolean;
  frequencia_unico?: string;
  frequencia_horas?: string;
}): boolean {
  if (row.active === false) return false;
  if (row.frequencia === '31') return isUniqueCampaignPending(row);
  return row.expirar !== true;
}
